import type { Registry as Services } from '@ember/service';
import { sentenceCase } from 'change-case';
import { tKey } from 'ember-intl';
import {
  isMappingChangeDetailArray,
  type MappingChangeDetail,
} from 'lumen/models/activity-log';
import {
  type Breakdown,
  BREAKDOWN_TRANSLATIONS,
} from 'lumen/utils/central-mapping-constants';
import type { ActivityActionTranslationKeys } from 'lumen/utils/constants/activity-action-constants';

const MISSING_KEY = tKey('general.missing');
const NO_KEY = tKey('general.no');
const YES_KEY = tKey('general.yes');

/**
 * Normalizes the two `changes` shapes activity logs send (see `ChangeDialect`)
 * into one type, so the two render targets (expand rows, "view all changes"
 * modal) can be thin adapters over a single source of truth.
 *
 * Type alias, not interface: only aliases get TS's implicit index signature,
 * which lets these pass straight into PrismTable's `CustomEmberTableRow[]`.
 */
export type DisplayChange = {
  breakdown?: string;
  entryValue?: string;
  field: string;
  from: string;
  to: string;
};

/**
 * One backend mechanism's vocabulary for describing a change. Deliberately
 * non-exhaustive — unmapped fields fall back to a sentence-cased identifier
 * instead of breaking the row (see `labelField`).
 */
export interface ChangeDialect {
  /** field -> tKey */
  fieldLabels: Record<string, string>;
  /** Names the mechanism in fallback reports; not user-facing. */
  name: string;
  /** value -> tKey, applied to any field in this dialect */
  sharedValueLabels: Record<string, string>;
  /** field -> value -> tKey, checked before `sharedValueLabels` */
  valueLabelsByField: Record<string, Record<string, string>>;
}

interface LabelFieldOptions {
  dialect: ChangeDialect;
  errorTracking: Services['error-tracking'];
  field: string;
  intl: Services['intl'];
}

interface LabelValueOptions {
  dialect: ChangeDialect;
  errorTracking: Services['error-tracking'];
  field: string;
  intl: Services['intl'];
  value: unknown;
}

interface ToDisplayChangesOptions {
  dialect: ChangeDialect;
  errorTracking: Services['error-tracking'];
  intl: Services['intl'];
}

/**
 * Dedupes repeated gap reports: a 190-item batch with one unmapped field
 * would otherwise fire hundreds of events. Sentry's own Dedupe integration
 * only catches *consecutive* duplicates, so it doesn't cover this case.
 */
const reportedGaps = new Set<string>();

function reportGapOnce({
  errorTracking,
  key,
  message,
}: {
  errorTracking: Services['error-tracking'];
  key: string;
  message: string;
}): void {
  if (reportedGaps.has(key)) {
    return;
  }

  reportedGaps.add(key);
  errorTracking.captureMessage(message);
}

/**
 * The field-tuple and mapping-changes dialects are told apart by shape, not
 * category — this checks for the field-tuple dialect's `[old, new]` pair.
 */
export function isChangeTuple(
  value: unknown,
): value is [string | null, string | null] {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    (typeof value[0] === 'string' || value[0] === null) &&
    (typeof value[1] === 'string' || value[1] === null)
  );
}

/**
 * Falls back to sentence case for unmapped identifiers, matching the casing
 * of the mapped labels beside them in the same column. Deliberately unlike
 * `central-mapping-upload-error-message.ts`, which capital-cases because it
 * labels spreadsheet headers, not table rows.
 */
export function labelField({
  dialect,
  errorTracking,
  field,
  intl,
}: LabelFieldOptions): string {
  const translationKey = dialect.fieldLabels[field];

  if (translationKey) {
    return intl.t(translationKey);
  }

  reportGapOnce({
    errorTracking,
    key: `field:${dialect.name}:${field}`,
    message: `Activity history: no label for ${dialect.name} change field "${field}"`,
  });

  return sentenceCase(field);
}

/**
 * Field-specific overrides win over dialect-wide ones: a value's meaning
 * depends on its field (the same "True" means "Allow Hotel Editors and
 * Admins to map" for the mapping permission, plain "Yes" elsewhere).
 *
 * Unrecognized values are never sentence-cased, unlike field identifiers —
 * a value may be arbitrary real data (a rate code, a name), not a code id.
 */
export function labelValue({
  dialect,
  errorTracking,
  field,
  intl,
  value,
}: LabelValueOptions): string {
  if (value === null || value === undefined) {
    return intl.t(MISSING_KEY);
  }

  if (typeof value === 'boolean') {
    return intl.t(value ? YES_KEY : NO_KEY);
  }

  if (typeof value === 'string') {
    const translationKey =
      dialect.valueLabelsByField[field]?.[value] ??
      dialect.sharedValueLabels[value];

    return translationKey ? intl.t(translationKey) : value;
  }

  if (typeof value === 'number') {
    return String(value);
  }

  if (
    typeof value === 'object' &&
    'name' in value &&
    typeof value.name === 'string'
  ) {
    return value.name;
  }

  // Never blindly stringify — that's the exact `[object Object]` bug this
  // path already had to fix once.
  reportGapOnce({
    errorTracking,
    key: `value:${dialect.name}:${field}`,
    message: `Activity history: unrenderable ${dialect.name} value for field "${field}"`,
  });

  return intl.t(MISSING_KEY);
}

/**
 * Field-tuple shape comes from the backend's automatic audit-log
 * registration for any category. Non-tuple entries belong to another
 * dialect and are dropped rather than mis-rendered as an old/new pair.
 */
export function toDisplayChangesFromTuples({
  changes,
  dialect,
  errorTracking,
  intl,
}: ToDisplayChangesOptions & {
  changes: Record<string, unknown> | null;
}): DisplayChange[] {
  return Object.entries(changes ?? {})
    .filter((entry): entry is [string, [string | null, string | null]] =>
      isChangeTuple(entry[1]),
    )
    .map(([field, [from, to]]) => ({
      field: labelField({ dialect, errorTracking, field, intl }),
      from: labelValue({ dialect, errorTracking, field, intl, value: from }),
      to: labelValue({ dialect, errorTracking, field, intl, value: to }),
    }));
}

/**
 * Adapts the mapping-changes dialect: `changes.mapping_changes[]`, built
 * explicitly for batched central mapping edits.
 */
export function toDisplayChangesFromItemized({
  details,
  dialect,
  errorTracking,
  intl,
}: ToDisplayChangesOptions & {
  details: MappingChangeDetail[];
}): DisplayChange[] {
  return details.map((change) => ({
    breakdown: labelBreakdown({
      breakdown: change.breakdown,
      errorTracking,
      intl,
    }),
    entryValue: change.entry_value ?? intl.t(MISSING_KEY),
    field: labelField({ dialect, errorTracking, field: change.field, intl }),
    from: labelValue({
      dialect,
      errorTracking,
      field: change.field,
      intl,
      value: change.old,
    }),
    to: labelValue({
      dialect,
      errorTracking,
      field: change.field,
      intl,
      value: change.new,
    }),
  }));
}

function isKnownBreakdown(value: string): value is Breakdown {
  return value in BREAKDOWN_TRANSLATIONS;
}

/** Reuses Central Mapping's own breakdown names so both features agree. */
function labelBreakdown({
  breakdown,
  errorTracking,
  intl,
}: {
  breakdown: string | undefined;
  errorTracking: Services['error-tracking'];
  intl: Services['intl'];
}): string {
  if (!breakdown) {
    return intl.t(MISSING_KEY);
  }

  if (isKnownBreakdown(breakdown)) {
    return intl.t(BREAKDOWN_TRANSLATIONS[breakdown]);
  }

  reportGapOnce({
    errorTracking,
    key: `breakdown:${breakdown}`,
    message: `Activity history: no label for breakdown "${breakdown}"`,
  });

  return sentenceCase(breakdown);
}

/**
 * Action keys whose sentence needs a `count`. The backend always sends the
 * full change list for these, so a missing `values.count` can be derived
 * from the list's length (see `withDerivedCount`).
 */
const BATCH_ACTION_KEYS = new Set<ActivityActionTranslationKeys>([
  'central_mapping_relation_changed_itemized',
  'central_mapping_relation_changed_count',
  'central_mapping_category_changed_itemized',
  'central_mapping_category_changed_count',
]);

/** Derives `count` only when missing — a real `values.count` from the
 * backend always wins. */
export function withDerivedCount({
  actionKey,
  changes,
  values,
}: {
  actionKey: ActivityActionTranslationKeys | undefined;
  changes: Record<string, unknown> | null;
  values: Record<string, string>;
}): Record<string, string> {
  const mappingChanges = changes?.['mapping_changes'];

  if (
    !actionKey ||
    !BATCH_ACTION_KEYS.has(actionKey) ||
    values['count'] ||
    !isMappingChangeDetailArray(mappingChanges)
  ) {
    return values;
  }

  return { ...values, count: String(mappingChanges.length) };
}

/**
 * Gated on payload shape as well as action key, so an unrelated future
 * batch key degrades to a plain sentence with no icon, not an empty modal.
 */
export function readItemizedChanges({
  actionKey,
  changes,
}: {
  actionKey: ActivityActionTranslationKeys | undefined;
  changes: Record<string, unknown> | null;
}): MappingChangeDetail[] | null {
  if (!actionKey || !BATCH_ACTION_KEYS.has(actionKey)) {
    return null;
  }

  const mappingChanges = changes?.['mapping_changes'];

  return isMappingChangeDetailArray(mappingChanges) ? mappingChanges : null;
}
